import React, { useState, useEffect } from "react";
import { Session } from "../../../../services/Dtos";
import { VStack, HStack, Box } from "@chakra-ui/react";
import { getTodayDate } from "../../../../services/Utils";
import CustomAlert from "../../../CustomUi/CustomAlert";

import useIcons from "../../../../hooks/useIcons";
import CustomModal from "../../../CustomUi/CustomModal";
import TextInput from "../../../CustomUi/TextInput";
import CustomNumberInput from "../../../CustomUi/CustomNumberInput";
import { useConfirmDialog } from "../../../../hooks/useConfirmDialog";
import { useFormChanges } from "../../../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../../../hooks/useFormFieldHandler";
import { useSessionService } from "../../../../hooks/Domain/useSessionService";
import { validateDateRange } from "../../../../services/validation";
import InlineSeparator from "../../../CustomUi/InlineSeparator";
import Info from "../../../CustomUi/Info";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sessionToEdit: Session | null;
  nextOrder: number;
}

const SessionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  sessionToEdit,
  nextOrder,
}) => {
  const {
    createSession,
    updateSession,
    deleteSession,
    openDeleteDialog,
    setOpenDeleteDialog,
    errors,
    clearErrors,
    isLoading,
  } = useSessionService();

  const getInitialSessionFormState = (): Session => {
    const today = getTodayDate();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split("T")[0];

    return {
      id: "",
      sessionName: "",
      sessionStartDate: today,
      sessionEndDate: tomorrowStr,
      schoolId: "",
      order: nextOrder,
      createdAt: "",
      updatedAt: "",
    };
  };

  const initialSessionFormState = getInitialSessionFormState();

  const [sessionFormState, setSessionFormState] = useState<Session>(
    initialSessionFormState
  );
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const icons = useIcons();

  // Initialize confirmation dialog
  const { confirmUnsavedChanges } = useConfirmDialog();

  // Define field configurations for form validation (only the fields we want to validate)
  const fieldConfigs = {
    sessionName: { type: "text" as const, required: true, maxLength: 50 },
    order: { type: "number" as const, required: true },
    sessionStartDate: { type: "date" as const, required: true },
    sessionEndDate: {
      type: "date" as const,
      required: true,
      customValidation: (value: string) => {
        return validateDateRange(
          value,
          sessionFormState.sessionStartDate,
          true
        );
      },
    },
  } as const;

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: sessionToEdit,
      currentData: sessionFormState,
      isEditMode: !!sessionToEdit,
    });

  // Use the form field handler hook
  const { handleFieldChange } = useFormFieldHandler({
    setFormState: setSessionFormState,
    setTouchedFields,
    integerFields: ["order"],
  });

  // Real-time validation using the hook's validateField
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof Session;
    return validateField(fieldKey, value);
  };

  // Clear errors when modal opens
  useEffect(() => {
    if (isOpen) {
      clearErrors();
      setTouchedFields(new Set());
    }
  }, [isOpen, clearErrors]);

  useEffect(() => {
    if (sessionToEdit) {
      setSessionFormState(sessionToEdit);
    } else {
      setSessionFormState(getInitialSessionFormState());
    }
    setTouchedFields(new Set());
  }, [sessionToEdit, isOpen]);

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      sessionFormState[fieldName as keyof Session]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      sessionFormState[fieldName as keyof Session]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  const handleSave = async () => {
    if (hasValidationErrorsValue || !hasChanges) return;

    if (sessionToEdit) {
      const response = await updateSession(sessionToEdit.id, sessionFormState);
      if (response.result.succeeded) {
        onClose();
      }
    } else {
      const response = await createSession(sessionFormState);
      if (response.result.succeeded) {
        onClose();
      }
    }
  };

  const handleDeleteSession = async () => {
    if (!sessionToEdit) return;

    const response = await deleteSession(sessionToEdit.id);
    if (response.result.succeeded) {
      onClose();
    }
  };

  const handleClose = async () => {
    if (hasChanges && sessionToEdit) {
      const confirmed = await confirmUnsavedChanges();
      if (confirmed) {
        clearErrors();
        onClose();
      }
    } else {
      clearErrors();
      onClose();
    }
  };

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        handleClose={handleClose}
        handleAction={handleSave}
        handleDelete={handleDeleteSession}
        deleteMessage="All related session records and student assignments will be lost permanently"
        isLoading={isLoading}
        hasChanges={hasChanges}
        hasValidationErrors={hasValidationErrorsValue}
        isDisabled={!hasChanges || hasValidationErrorsValue || isLoading}
        title={sessionToEdit ? "Edit Session" : "Add New Session"}
        icon={icons.main.session}
        mode={sessionToEdit ? "edit" : "add"}
        openDeleteDialog={openDeleteDialog}
        setOpenDeleteDialog={setOpenDeleteDialog}
        actionText={sessionToEdit ? "Save Changes" : "Save Session"}
      >
        {/* Show only API/server errors */}
        {errors.length > 0 && (
          <VStack gap={2} mb={4} align={"stretch"}>
            {errors.slice(0, 3).map((error, index) => (
              <CustomAlert key={index} message={error} tight />
            ))}
          </VStack>
        )}

        <VStack gap={6} align="stretch">
          {/* Session Information Section */}
          <Box>
            <InlineSeparator label="Session Information" />

            <TextInput
              label="Session Name"
              name="sessionName"
              type="text"
              value={sessionFormState.sessionName}
              onChange={handleFieldChange}
              placeholder="e.g. 2020/2021"
              required
              error={
                showFieldError("sessionName")
                  ? getFieldError("sessionName")
                  : ""
              }
              invalid={showFieldError("sessionName")}
            />
          </Box>

          {/* Date Range Section */}
          <Box>
            <InlineSeparator label="Date Range" />

            <HStack
              gap={4}
              w="full"
              flexDirection={["column", "row"]}
              alignItems={["stretch", "center"]}
            >
              <TextInput
                label="Start Date"
                name="sessionStartDate"
                type="date"
                value={sessionFormState.sessionStartDate}
                onChange={handleFieldChange}
                error={
                  showFieldError("sessionStartDate")
                    ? getFieldError("sessionStartDate")
                    : ""
                }
                invalid={showFieldError("sessionStartDate")}
              />

              <TextInput
                label="End Date"
                name="sessionEndDate"
                type="date"
                value={sessionFormState.sessionEndDate}
                onChange={handleFieldChange}
                error={
                  showFieldError("sessionEndDate")
                    ? getFieldError("sessionEndDate")
                    : ""
                }
                invalid={showFieldError("sessionEndDate")}
              />
            </HStack>
          </Box>
          <CustomNumberInput
            label="Order"
            value={sessionFormState.order.toString()}
            onChange={(value) => {
              setSessionFormState((prev) => ({
                ...prev,
                order: parseInt(value) || 0,
              }));
              setTouchedFields((prev) => new Set(prev).add("order"));
            }}
            min={1}
            error={showFieldError("order") ? getFieldError("order") : ""}
            info={
              <Info content="The order of the session in a list of sessions. This helps with arranging the sessions the way you want." />
            }
          />
        </VStack>
      </CustomModal>
    </>
  );
};

export default SessionModal;
